import React from "react";
import { Link } from "react-router-dom";
import ToolTip from "src/components/reusable/tool-tip";
import jsCookie from "js-cookie";
import { Megaphone } from "lucide-react";

type Agent = {
  agentName: string;
  title: string;
  description: string;
  icon: string;
  bgClass: string;
  className: string;
  backgroundImage: string;
  agentLink?: string;
  navLink?: string;
  section_name?: string;
  disabled?: boolean;
  hoverTitle: string;
  hoverDesc: string;
};

type AgentCardProps = {
  agent: Agent;
};

const AgentCard: React.FC<AgentCardProps> = ({ agent }) => {
  const userId = jsCookie.get("user_id");
  const linkTarget = agent.disabled ? "" : agent.navLink || `/ai-agent?agent=${agent.agentLink}`;

  const CardInner = (
    <div
      className={`group relative flex flex-1 rounded-lg overflow-hidden hover:shadow-lg transition-shadow cursor-pointer h-full ${agent.className}`}
    >
      <div
        className={`flex flex-1 bg-gradient-to-br ${agent.bgClass} px-3 py-6 h-full`}
        style={{
          backgroundImage: `url(${agent.backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* === flex-col container === */}
        <div className="flex flex-1 flex-col justify-between gap-3 items-stretch rounded-lg h-full">
          {(agent.agentName === "Marketing Agent"  )? (
            <>
              {/* ——— top block (icon + title) ——— */}
              <div className="flex flex-col">
                <div className="flex flex-col">
                  <img
                    src={agent.icon}
                    alt={agent.title}
                    className="w-8 h-8 mb-2"
                  />
                  <div className="bg-white p-0.5 w-fit rounded backdrop-blur-sm">
                    <h3 className="text-3xl text-[#373D3F]">{agent.title}</h3>
                  </div>
                </div>
              </div>
              <div>
                {/* ✅ only this button navigates */}
                <Link to={linkTarget}>
                  <button className="font-inter font-medium text-[16px] leading-[28px] tracking-normal align-middle text-white bg-[#442873] px-2 py-1 rounded-md mr-3">
                    Start Campaign Intake
                  </button>
                </Link>

                <Link to={`/marketing-agent/campaigns`}>
                  <button className="font-inter font-medium text-[16px] leading-[28px] align-middle tracking-normal text-[#442873] bg-white px-2 py-1 rounded-md">
                      View Dashboard
                  </button>
                </Link>
                
              </div>
            </>
          ) : ((agent.agentName === "MR Insights Agent" || agent.agentName === "Email Automation Agent")  ? 
              <>
                  {/* ——— top block (icon + title) ——— */}
                  <div className="transition-opacity duration-300 group-hover:opacity-0">
                    <div className="flex flex-col">
                      <img
                        src={agent.icon}
                        alt={agent.title}
                        className="w-7 h-7 mb-2"
                      />
                      <div className="bg-white p-0.5 w-fit rounded backdrop-blur-sm">
                        <h3 className="text-3xl text-[#373D3F]">{agent.title}</h3>
                      </div>
                    </div>
                  </div>
                  <p className="text-[#373D3F] text-sm transition-opacity duration-300 group-hover:opacity-0">
                    {agent.description}
                  </p>
                </>
            :
            <>
              {/* ——— top block (icon + title) ——— */}
              <div className="transition-opacity duration-300 group-hover:opacity-0">
                <div className="flex flex-col">
                  <img
                    src={agent.icon}
                    alt={agent.title}
                    className="w-10 h-10 mb-2"
                  />
                  <div className="bg-white p-0.5 w-fit rounded backdrop-blur-sm">
                    <h3 className="text-3xl text-[#373D3F]">{agent.title}</h3>
                  </div>
                </div>
              </div>
              <p className="text-[#373D3F] text-sm transition-opacity duration-300 group-hover:opacity-0">
                {agent.description}
              </p>
            </>
          )}
        </div>

        {/* ——— hover overlay ——— */}
        {agent.agentName == "Marketing Agent" ? (
          <div className="absolute inset-0 flex flex-col justify-center items-start text-white text-left bg-[#FFF5E2] bg-opacity-100 px-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <h3 className="text-[32px] mb-3 font-semibold text-[#442873]">
              {agent.title}
            </h3>
            <h5 className="text-[20px] font-semibold text-[#373D3F]">
              {agent.hoverTitle}
            </h5>
            <p className="text-[20px] text-[#373D3F]">{agent.hoverDesc}</p>
            <div className="mt-3">
            <Link to={linkTarget}>
                  <button className="font-inter font-medium text-[16px] leading-[28px] tracking-normal align-middle text-white bg-[#442873] px-2 py-1 rounded-md mr-3">
                    Start Campaign Intake
                  </button>
                </Link>

                <Link to={`/marketing-agent/campaigns`}>
                  <button className="font-inter font-medium text-[16px] leading-[28px] align-middle tracking-normal text-[#442873] bg-white px-2 py-1 rounded-md">
                      View Dashboard
                  </button>
                </Link>
            </div>
          </div>
          ) : (
          
          <div className="absolute inset-0 flex flex-col justify-center items-start text-white text-left bg-[#FFF5E2] bg-opacity-100 px-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <h3 className="text-[32px] mb-3 font-semibold text-[#442873]">
              {agent.title}
            </h3>
            <h5 className="text-[20px] font-semibold text-[#373D3F]">
              {agent.hoverTitle}
            </h5>
            <p className="text-[20px] text-[#373D3F]">{agent.hoverDesc}</p>
          </div>
        
        )}
      </div>
    </div>
  );

  // ✅ For others: full card is clickable
  return agent.section_name === "ip_experts" ? (
    <ToolTip title="Coming Soon" placement="bottom">
      <Link to={linkTarget} className="h-full">
        {CardInner}
      </Link>
    </ToolTip>
  ) : (
    <Link to={linkTarget} className="h-full">
      {CardInner}
    </Link>
  );
};

export default AgentCard;
